// CreateCourse.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiTrash2,
  FiMove,
  FiPlus,
} from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import SharedLayout from "./SharedLayout";
import "./App.css";
import axios from "axios";
import DatePicker from "react-datepicker";
import { tr } from "date-fns/locale";
import InfoModal from "./modals/InfoModals";
import LinkModal from "./modals/LinkModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_TOTAL = 30 * 1024 * 1024;
const MAX_COVER_MB = 2;

const filenameFromUrl = (url) => {
  try {
    const u = new URL(url, window.location.origin);
    const name = u.pathname.split("/").pop() || "";
    return decodeURIComponent(name);
  } catch {
    const parts = (url || "").split("/");
    return decodeURIComponent(parts[parts.length - 1] || url);
  }
};

const sanitizeText = (text) => {
  if (!text) return "";
  return text
    .replace(/\r?\n|\r/g, " ")
    .replace(/\t/g, " ")
    .replace(/\s\s+/g, " ")
    .trim();
};

const CreateCourse = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [currentDateTime, setCurrentDateTime] = useState({
    date: "",
    time: "",
    datetime: null,
  });

  // ---- Gün & soru şablonları
  const questionTemplate = () => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    text: "",
    options: ["", ""],
    correctOption: null,
  });

  const dayTemplate = () => ({
    lessonTitle: "",
    topicContent: "",
    assessment: {
      enabled: false,
      questions: [],
    },
    media: { documents: [], medias: [], links: [] },
  });

  const ALLOW_MULTI_QUESTIONS = false;

  // Step-2 gün verileri + aktif gün
  const [lessons, setLessons] = useState([dayTemplate()]);
  const [activeDay, setActiveDay] = useState(0);

  // Gün sekmeleri drag–drop için
  const [dragIndex, setDragIndex] = useState(null);

  const [linkModalOpen, setLinkModalOpen] = useState(false);

  // Step 1 & 3 genel form verileri
  const [formData, setFormData] = useState({
    courseName: "",
    description: "",
    coverImage: "",
    courseLength: "1",
    deliveryDate: "",
    repostEvery: "daily",
    deliveryTime: "",
    whatsappNotification: true,
  });

  const [coverPreview, setCoverPreview] = useState("");

  // Info modal state
  const [info, setInfo] = useState({
    open: false,
    type: "info",
    title: "",
    message: "",
  });
  const showInfo = (type, title, message) =>
    setInfo({ open: true, type, title, message });
  const closeInfo = () => setInfo((p) => ({ ...p, open: false }));

  // Toplam boyut state
  const calcTotalSize = (lessonsArr) => {
    return lessonsArr.reduce((sum, day) => {
      const docSize = (day.media.documents || []).reduce(
        (s, f) => s + (f.size || 0),
        0
      );
      const mediaSize = (day.media.medias || []).reduce(
        (s, f) => s + (f.size || 0),
        0
      );
      return sum + docSize + mediaSize;
    }, 0);
  };
  const [totalSize, setTotalSize] = useState(0);

  // Quiz Validation
  const isCurrentDayValid  = () => {
    const day = lessons[activeDay];
    if (!day) return false;

    if (!day.assessment.enabled) return true;
    const questions = day.assessment.questions || [];
    if (questions.length === 0) return false;
    const q = questions[0];
    if (!q.text || q.text.trim() === '') return false;

    if (!q.options || q.options.length < 2) return false;
    const hasEmptyOption = q.options.some(opt => !opt || opt.trim() === '');
    if (hasEmptyOption) return false;

    if (q.correctOption === null || q.correctOption === undefined) return false;

    return true;
  }

  useEffect(() => {
    setTotalSize(calcTotalSize(lessons));
  }, [lessons]);

  useEffect(() => {
    const now = new Date();
    const date = now.toISOString().split("T")[0];
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const time = `${hours}:${minutes}`;
    setCurrentDateTime({ date, time, datetime: now });
  }, []);

  useEffect(() => {
    const len = Math.max(1, parseInt(formData.courseLength || "1", 10));
    setLessons((prev) => {
      let updated = [...prev];
      if (updated.length < len) {
        while (updated.length < len) updated.push(dayTemplate());
      } else if (updated.length > len) {
        updated = updated.slice(0, len);
      }
      return updated;
    });
    setActiveDay((d) => Math.min(d, len - 1));
  }, [formData.courseLength]);

  // ---- Step 3: tarih/saat kontrol & handler'lar
  const isDateTimeValid = (date, time) => {
    if (!date || !time) return false;
    const selected = new Date(`${date}T${time}`);
    const now = currentDateTime.datetime;
    return selected >= now;
  };

  // Step-3 gruplar
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(0); // or null

  const fetchGroups = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/groups/${userId}`);

      const formattedGroups = response.data.map((group) => ({
        id: group.id,
        name: group.name,
        members: group.members.map((member) => ({
          id: member.id,
          number: member.number,
        })),
      }));

      setGroups(formattedGroups);
    } catch (err) {
      console.error("Error fetching groups:", err);
      showInfo(
        "error",
        "Grup listesi alınamadı",
        "Gruplar yüklenirken bir hata oluştu."
      );
    }
  }, [userId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ---- Step 1 genel input/cover
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Kapak resmi (max 2MB) → backend'e upload → URL kaydet
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const limit = MAX_COVER_MB * 1024 * 1024;
    if (file.size > limit) {
      showInfo(
        "warning",
        "Dosya çok büyük",
        `Kapak resmi en fazla ${MAX_COVER_MB}MB olabilir.`
      );
      e.target.value = "";
      return;
    }

    const form = new FormData();
    form.append("file", file);

    try {
      const { data } = await axios.post(`${BACKEND_URL}/upload/cover`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const coverUrl = data.file?.url || data.url;

      setFormData((prev) => ({ ...prev, coverImage: coverUrl }));
      setCoverPreview(URL.createObjectURL(file));
    } catch (err) {
      console.error("Cover upload error:", err);
      showInfo(
        "error",
        "Kapak yüklenemedi",
        err.response?.data?.message || err.message || "Bilinmeyen hata"
      );
    }
  };

  const removeMediaItem = (dayIdx, key, idx) => {
    const fileObj = lessons[dayIdx].media[key][idx];
    const url = typeof fileObj === "string" ? fileObj : fileObj.url;
    const filename = url.split("/").pop();

    setLessons((prev) =>
      prev.map((d, i) =>
        i === dayIdx
          ? {
              ...d,
              media: {
                ...d.media,
                [key]: d.media[key].filter((_, j) => j !== idx),
              },
            }
          : d
      )
    );

    axios
      .delete(`${BACKEND_URL}/upload/${encodeURIComponent(filename)}`)
      .then((res) => console.log("Silindi:", filename, res.data))
      .catch((err) => console.error("Sunucudan silme hatası:", err));
  };

  // ---- Upload helper
  const uploadFile = async (file, type) => {
    // Cloudinary limiti: 10MB
    const CLOUDINARY_MAX_SIZE = 10 * 1024 * 1024; // 10MB
    
    // Tek dosya boyutu kontrolü
    if (file.size > CLOUDINARY_MAX_SIZE) {
      showInfo(
        "error",
        "Dosya çok büyük",
        "Dosya boyutu çok büyük. Maksimum 10MB olabilir."
      );
      return null;
    }
    
    // Toplam dosya boyutu kontrolü
    if (totalSize + file.size > MAX_TOTAL) {
      showInfo(
        "warning",
        "Limit aşıldı",
        "Toplam dosya boyutu 30MB'ı geçemez!"
      );
      return null;
    }

    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/upload/${type}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return { url: data.file.url, size: data.file.size };
    } catch (err) {
      console.error(`${type} upload error:`, err);
      
      // Backend'den gelen error_code'a göre Türkçe mesaj göster
      const errorCode = err.response?.data?.error_code;
      const backendMessage = err.response?.data?.message || err.response?.data?.error;
      
      let errorTitle = "Yükleme hatası";
      let errorMessage = `${type} yüklenemedi.`;
      
      switch (errorCode) {
        case 'NO_FILE_UPLOADED':
          errorTitle = "Dosya seçilmedi";
          errorMessage = "Lütfen bir dosya seçiniz.";
          break;
        case 'FILE_TOO_LARGE':
        case 'FILE_SIZE_EXCEEDED':
          errorTitle = "Dosya çok büyük";
          errorMessage = "Dosya boyutu çok büyük. Maksimum 10MB olabilir.";
          break;
        case 'INVALID_FILE_TYPE':
          errorTitle = "Geçersiz dosya tipi";
          errorMessage = "Geçersiz dosya tipi. Sadece PDF, DOC, DOCX dosyaları yüklenebilir.";
          break;
        case 'CLOUDINARY_UPLOAD_FAILED':
          errorTitle = "Yükleme başarısız";
          errorMessage = "Dosya yüklenirken bir hata oluştu. Lütfen tekrar deneyin.";
          break;
        case 'TOTAL_SIZE_EXCEEDED':
          errorTitle = "Limit aşıldı";
          errorMessage = "Toplam dosya boyutu 30MB'ı geçemez!";
          break;
        default:
          // Backend'den gelen mesajı kullan veya varsayılan mesaj
          if (backendMessage) {
            errorMessage = backendMessage;
          }
      }
      
      showInfo("error", errorTitle, errorMessage);
      return null;
    }
  };

  // ---- Step 2: Gün alanı helper'ları
  const updateLessonField = (idx, field, value) => {
    setLessons((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d))
    );
  };

  const toggleAssessmentEnabled = (idx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== idx) return d;
        const enabled = !d.assessment.enabled;
        const nextQuestions =
          enabled && d.assessment.questions.length === 0
            ? [questionTemplate()]
            : d.assessment.questions.slice(
                0,
                ALLOW_MULTI_QUESTIONS ? undefined : 1
              );
        return {
          ...d,
          assessment: {
            ...d.assessment,
            enabled,
            questions: nextQuestions,
          },
        };
      })
    );
  };

  const deleteQuestion = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.filter((_, j) => j !== qIdx);
        const shouldDisableAssessment = qs.length === 0;

        return { ...d, assessment: { ...d.assessment, questions: qs, enabled: shouldDisableAssessment ? false : d.assessment.enabled } };
      })
    );
  };

  // Soru metni
  const updateQuestionField = (dayIdx, qIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, text: value } : q
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  // Seçenek işlemleri
  const updateOption = (dayIdx, qIdx, optIdx, value) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = [...q.options];
          ops[optIdx] = value;
          return { ...q, options: ops };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const addOption = (dayIdx, qIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          if (q.options.length >= 5) return q;
          return { ...q, options: [...q.options, ""] };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const removeOption = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) => {
          if (j !== qIdx) return q;
          const ops = q.options.filter((_, k) => k !== optIdx);
          let correct = q.correctOption;
          if (correct === optIdx) correct = null;
          else if (correct > optIdx) correct = correct - 1;
          return { ...q, options: ops, correctOption: correct };
        });
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  const setCorrect = (dayIdx, qIdx, optIdx) => {
    setLessons((prev) =>
      prev.map((d, i) => {
        if (i !== dayIdx) return d;
        const qs = d.assessment.questions.map((q, j) =>
          j === qIdx ? { ...q, correctOption: optIdx } : q
        );
        return { ...d, assessment: { ...d.assessment, questions: qs } };
      })
    );
  };

  // Gün silme
  const deleteDay = (idx) => {
    if (lessons.length <= 1) return;
    setLessons((prev) => {
      const updated = prev.filter((_, i) => i !== idx);
      setActiveDay((a) => {
        if (a === idx) return Math.max(0, idx - 1);
        if (a > idx) return a - 1;
        return a;
      });
      setFormData((p) => ({ ...p, courseLength: String(updated.length) }));
      return updated;
    });
  };

  // Gün drag–drop
  const onDragStart = (idx) => setDragIndex(idx);
  const onDragEnd = () => setDragIndex(null);
  const onDrop = (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) return;
    setLessons((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIndex, 1);
      arr.splice(dropIdx, 0, moved);
      setActiveDay((a) => {
        if (a === dragIndex) return dropIdx;
        if (dragIndex < dropIdx && a > dragIndex && a <= dropIdx) return a - 1;
        if (dragIndex > dropIdx && a >= dropIdx && a < dragIndex) return a + 1;
        return a;
      });
      return arr;
    });
    setDragIndex(null);
  };

  // Step geçişleri
  const nextStep = () => setCurrentStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 1));

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (mode = "PUBLISHED") => {
    // Validation
    if (!isDateTimeValid(formData.deliveryDate, formData.deliveryTime)) {
      showInfo(
        "warning",
        "Geçersiz tarih/zaman",
        "Geçmiş gün/zaman seçemezsiniz!"
      );
      return;
    }

    if (!selectedGroupId) {
      showInfo("warning", "Grup seçimi gerekli", "Lütfen bir grup seçiniz!");
      return;
    }

    if (!formData.courseName.trim()) {
      showInfo("warning", "Eksik bilgi", "Lütfen kurs adını giriniz!");
      return;
    }

    const hasEmptyLesson = lessons.some(
      (lesson) => !lesson.lessonTitle?.trim() || !lesson.topicContent?.trim()
    );

    if (hasEmptyLesson) {
      showInfo(
        "warning",
        "Eksik içerik",
        "Lütfen tüm dersler için başlık ve içerik giriniz!"
      );
      return;
    }

    setIsSubmitting(true);

    console.log(groups.find((group) => group.id === selectedGroupId));

    console.log(
      groups
        .find((group) => group.id === selectedGroupId)
        ?.members.map((member) => member.id)
    );

    //console.log(lessons)

    const sanitizedLessons = lessons.map((lesson, index) => ({
      title: sanitizeText(lesson.lessonTitle),
      content: sanitizeText(lesson.topicContent),
      day: index + 1,
      document: lesson.media.documents[0]?.url || null,
      media: lesson.media.medias[0]?.url || null,
      externalLink: lesson.media.links[0] || null,
      quiz:
        lesson.assessment.enabled && lesson.assessment.questions[0]
          ? {
              question: sanitizeText(lesson.assessment.questions[0].text || ""),
              options: lesson.assessment.questions[0].options.map((option) =>
                sanitizeText(option)
              ),
              correctOption:
                lesson.assessment.questions[0].options[
                  lesson.assessment.questions[0].correctOption
                ] || "",
            }
          : null,
    }));

    const requestBody = {
      courseData: {
        name: sanitizeText(formData.courseName),
        description: sanitizeText(formData.description),
        coverImage: formData.coverImage,
        adminId: Number(userId),
        status: mode,
      },
      lessonsData: sanitizedLessons,
      learnerIds:
        groups
          .find((group) => group.id === selectedGroupId)
          ?.members.map((member) => member.id) || [],
      scheduleTime: formData.deliveryTime,
      startDate: formData.deliveryDate,
      frequency: formData.repostEvery,
    };

    try {
      const response = await axios.post(
        `${BACKEND_URL}/courses/`,
        requestBody,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      showInfo(
        "success",
        "İşlem tamam",
        "İçerik başarılı şekilde oluşturuldu!"
      );
      resetForm();
      navigate(`/${userId}/dashboard`);
    } catch (error) {
      console.error("İçerik oluşturma hatası:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "İçerik oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.";
      showInfo("error", "Hata", errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setFormData({
      courseName: "",
      description: "",
      coverImage: "",
      deliveryDate: "",
      deliveryTime: "",
      repostEvery: "daily",
    });
    setCoverPreview("");
    setLessons([dayTemplate()]);
    setDragIndex(null);
    setActiveDay(0);
    setSelectedGroupId("");
  };

  // ------------------ RENDER ------------------
  return (
    <SharedLayout pageTitle="İÇERİK OLUŞTURMA">
      {/* Stepper */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-center overflow-x-auto no-scrollbar px-2">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <button
                  onClick={() => currentStep >= step && setCurrentStep(step)}
                  className={`px-4 sm:px-6 py-2 rounded-full ${
                    currentStep === step
                      ? "green-button text-white"
                      : "bg-gray-200 text-gray-600"
                  } ${
                    currentStep < step ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={currentStep < step}
                >
                  {step === 1 && "GİRİŞ"}
                  {step === 2 && "İÇERİK"}
                  {step === 3 && "YAYINLAMA"}
                </button>
                {step < 3 && (
                  <FiChevronRight
                    className="text-gray-400 hidden sm:inline-block"
                    size={20}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 sm:p-8 rounded-2xl shadow-sm">
        {/* STEP 1 */}
        {currentStep === 1 && (
          <div className="form-step space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center pb-4 sm:pb-6 mb-4 sm:mb-8 gap-3">
              <label className="text-gray-700 text-sm font-medium sm:mr-2">
                İÇERİK SÜRESİ:
              </label>
              <div className="flex items-center">
                <select
                  name="courseLength"
                  value={formData.courseLength}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full sm:ml-3 px-4 sm:px-6 py-1 text-md"
                >
                  {Array.from({ length: 10 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1}
                    </option>
                  ))}
                </select>
                <span className="text-gray-600 ml-3 text-md font-medium">
                  Tekrar
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK ADI:
                </label>
                <div className="border-b border-gray-300 pb-1">
                  <input
                    type="text"
                    name="courseName"
                    value={formData.courseName}
                    onChange={handleInputChange}
                    maxLength={60}
                    className="w-full p-2 outline-none bg-transparent"
                    placeholder="İçerik adı yazınız"
                    required
                  />
                  <div className="text-xs text-gray-500 text-right">
                    {formData.courseName.length}/60
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                  İÇERİK KAPAK RESMİ:
                </label>
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="w-full sm:w-32 h-40 sm:h-32 bg-gray-100 border border-gray-300 rounded flex items-center justify-center overflow-hidden">
                    {coverPreview || formData.coverImage ? (
                      <img
                        src={coverPreview || formData.coverImage}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">Önizleme</span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 mb-2">
                      Jpeg, gif veya png formatında, maksimum ~800×360px ve{" "}
                      {MAX_COVER_MB}MB olmalı.
                    </p>
                    <label className="inline-block text-blue-600 hover:text-blue-800 cursor-pointer text-sm underline">
                      RESMİ YÜKLE
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif, ,image/jpg"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-3 sm:mb-5">
                AÇIKLAMA:
              </label>
              <div className="relative">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={1024}
                  className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                  rows={4}
                  placeholder="İçerik açıklamasını yazınız"
                  required
                />
                <div className="text-xs text-gray-500 text-right mt-1">
                  {formData.description.length}/1024
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="w-full sm:w-auto px-6 py-2 green-button text-white rounded-full flex items-center justify-center"
                >
                  İLERİ <FiChevronRight className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {currentStep === 2 && (
          <div className="form-step">
            <div className="grid grid-cols-12 gap-4 sm:gap-6">
              {/* Sol: Gün sekmeleri */}
              <div className="col-span-12 md:col-span-3">
                <div className="p-3 bg-[#07183c] rounded-2xl">
                  <div className="text-white text-sm font-medium px-1 pb-2">
                    İçerik Günleri
                  </div>
                  <ul className="max-h-80 overflow-auto pr-1">
                    {lessons.map((_, i) => {
                      const isActive = activeDay === i;
                      return (
                        <li
                          key={i}
                          className="mb-2"
                          draggable
                          onDragStart={() => onDragStart(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => onDrop(i)}
                          onDragEnd={onDragEnd}
                        >
                          <div
                            className={`flex items-center justify-between px-3 py-2 rounded-lg transition-colors
                              ${
                                isActive
                                  ? "bg-white/10 text-white font-extrabold"
                                  : "text-white/70 hover:text-white font-medium"
                              }`}
                          >
                            <button
                              className="flex-1 text-left"
                              onClick={() => setActiveDay(i)}
                            >
                              {i + 1}.Tekrar
                            </button>
                            <div className="flex items-center gap-2 ml-3">
                              <FiMove
                                className="opacity-70 cursor-grab"
                                title="Drag to reorder"
                              />
                              <button
                                type="button"
                                onClick={() => deleteDay(i)}
                                className={`opacity-70 hover:opacity-100 ${
                                  lessons.length <= 1
                                    ? "cursor-not-allowed"
                                    : ""
                                }`}
                                disabled={lessons.length <= 1}
                                title={
                                  lessons.length <= 1
                                    ? "En az 1 gün olması gerekiyor"
                                    : "Günü sil"
                                }
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              {/* Sağ: Seçili güne ait içerik */}
              <div className="col-span-12 md:col-span-9 space-y-6">
                {/* Lesson Title */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    KONU BAŞLIĞI
                  </label>
                  <div className="border-b border-gray-300 pb-1">
                    <input
                      type="text"
                      value={lessons[activeDay]?.lessonTitle || ""}
                      onChange={(e) =>
                        updateLessonField(
                          activeDay,
                          "lessonTitle",
                          e.target.value
                        )
                      }
                      maxLength={60}
                      className="w-full p-2 outline-none bg-transparent"
                      placeholder="Konu başlığını yazınız"
                      required
                    />
                    <div className="text-xs text-gray-500 text-right">
                      {(lessons[activeDay]?.lessonTitle || "").length}/60
                    </div>
                  </div>
                </div>

                {/* Topic Content */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    KONU İÇERİK
                  </label>
                  <textarea
                    value={lessons[activeDay]?.topicContent || ""}
                    onChange={(e) => {
                      if (e.target.value.length <= 1024) {
                        updateLessonField(
                          activeDay,
                          "topicContent",
                          e.target.value
                        );
                      }
                    }}
                    maxLength={1024}
                    className="w-full p-3 border border-gray-300 rounded-lg bg-white"
                    rows={4}
                    placeholder="Konunuzun içeriğini yazınız"
                    required
                  />
                  <div className="text-xs text-gray-500 text-right mt-1">
                    {(lessons[activeDay]?.topicContent || "").length}/1024
                  </div>
                </div>

                {/* Assessment */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-gray-700 text-sm font-medium">
                      DEĞERLENDİRME
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={
                          lessons[activeDay]?.assessment.enabled || false
                        }
                        onChange={() => toggleAssessmentEnabled(activeDay)}
                      />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </div>

                  {lessons[activeDay]?.assessment.enabled && (
                    <div className="space-y-4">
                      {(lessons[activeDay]?.assessment.questions || [])
                        .slice(0, ALLOW_MULTI_QUESTIONS ? undefined : 1)
                        .map((q, qIdx) => (
                          <div
                            key={q.id}
                            className="border border-gray-200 bg-white rounded-xl shadow-sm p-4"
                          >
                            {/* Kart başlık */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
                              <div className="text-sm font-semibold text-gray-800">
                                SORU {qIdx + 1}
                              </div>
                              <button
                                type="button"
                                onClick={
                                  () => deleteQuestion(activeDay, qIdx)

                                }
                                className="self-start sm:self-auto text-red-500 hover:text-red-600 flex items-center gap-2 text-sm"
                                title="Delete question"
                              >
                                <FiTrash2 /> KALDIR
                              </button>
                            </div>

                            {/* Soru metni */}
                            <div className="mb-3">
                              <input
                                type="text"
                                value={q.text}
                                onChange={(e) =>
                                  updateQuestionField(
                                    activeDay,
                                    qIdx,
                                    e.target.value
                                  )
                                }
                                className="w-full p-2 border border-gray-300 rounded-lg bg-white"
                                placeholder="Sorunuzu yazınız..."
                                maxLength={140}
                              />
                              <div className="text-xs text-gray-500 text-right mt-1">
                                {q.text.length}/140
                              </div>
                            </div>

                            {/* Seçenekler */}
                            <div>
                              <label className="block text-gray-700 text-xs mb-2">
                                SEÇENEKLER
                              </label>
                              {q.options.map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2"
                                >
                                  <div className="flex items-center">
                                    <input
                                      type="radio"
                                      name={`correct-${activeDay}-${qIdx}`}
                                      checked={q.correctOption === optIdx}
                                      onChange={() =>
                                        setCorrect(activeDay, qIdx, optIdx)
                                      }
                                      className="mr-2"
                                    />
                                    <span className="text-xs text-gray-500 sm:hidden">
                                      Doğru
                                    </span>
                                  </div>
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) =>
                                      updateOption(
                                        activeDay,
                                        qIdx,
                                        optIdx,
                                        e.target.value
                                      )
                                    }
                                    maxLength={100}
                                    className="flex-1 min-w-0 p-2 border border-gray-300 rounded-lg bg-white"
                                    placeholder={`Seçenek ${optIdx + 1}`}
                                  />
                                  {optIdx >= 2 && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeOption(activeDay, qIdx, optIdx)
                                      }
                                      className="sm:ml-2 text-red-500 hover:text-red-700 self-start sm:self-auto"
                                    >
                                      <FiX size={18} />
                                    </button>
                                  )}
                                </div>
                              ))}

                              {q.options.length < 5 && (
                                <button
                                  onClick={() => addOption(activeDay, qIdx)}
                                  className="mt-2 px-4 py-2 border border-green-500 text-green-500 rounded-full text-sm hover:bg-green-50 transition-colors flex items-center gap-2"
                                >
                                  <FiPlus /> SEÇENEK EKLE
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>

                {/* Media Resources */}
                <div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <label className="text-gray-700 text-sm font-medium">
                      MEDYA KAYNAKLARI
                    </label>
                    <div className="flex items-center space-x-3 text-sm">
                      {/* Document Upload */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        DOKÜMAN YÜKLE
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "document");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        documents: [
                                          ...d.media.documents,
                                          uploaded,
                                        ],
                                      },
                                    }
                                  : d
                              )
                            );
                          }}
                        />
                      </label>

                      {/* Media Upload (video/audio/image) */}
                      <label className="text-blue-600 hover:underline cursor-pointer">
                        MEDYA YÜKLE
                        <input
                          type="file"
                          accept="video/*,audio/*,image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            const uploaded = await uploadFile(file, "media");
                            if (!uploaded) return;
                            setLessons((prev) =>
                              prev.map((d, i) =>
                                i === activeDay
                                  ? {
                                      ...d,
                                      media: {
                                        ...d.media,
                                        medias: [...d.media.medias, uploaded],
                                      },
                                    }
                                  : d
                              )
                            );
                          }}
                        />
                      </label>

                      {/* External Link */}
                      <button
                        type="button"
                        className="text-blue-600 hover:underline"
                        onClick={() => setLinkModalOpen(true)}
                      >
                        HARİCİ LİNK
                      </button>
                    </div>
                  </div>

                  {/* Listeler */}
                  <div className="grid md:grid-cols-3 gap-4 mt-4">
                    {/* Documents */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">
                        Dokümanlar
                      </div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.documents || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz doküman yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.documents.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "documents", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Medias */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Medyalar</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.medias || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz medya yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.medias.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u.url}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u.url}
                              >
                                {filenameFromUrl(u.url)}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "medias", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    {/* Links */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="font-semibold text-sm mb-2">Linkler</div>
                      <ul className="space-y-2">
                        {(lessons[activeDay]?.media?.links || []).length ===
                        0 ? (
                          <li className="text-xs text-gray-500">
                            Henüz link yok.
                          </li>
                        ) : (
                          lessons[activeDay].media.links.map((u, idx) => (
                            <li
                              key={idx}
                              className="flex items-center justify-between text-sm"
                            >
                              <a
                                href={u}
                                target="_blank"
                                rel="noreferrer"
                                className="truncate text-blue-600 hover:underline"
                                title={u}
                              >
                                {u}
                              </a>
                              <button
                                type="button"
                                className="ml-2 text-red-500 hover:text-red-600"
                                onClick={() =>
                                  removeMediaItem(activeDay, "links", idx)
                                }
                                title="Kaldır"
                              >
                                <FiTrash2 />
                              </button>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-3 text-xs text-gray-600">
                    Yüklenen: {(totalSize / (1024 * 1024)).toFixed(2)} MB / 30
                    MB
                  </div>
                  <div className="w-full bg-gray-200 h-2 rounded">
                    <div
                      className="bg-green-500 h-2 rounded"
                      style={{
                        width: `${Math.min(
                          (totalSize / MAX_TOTAL) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>

                  {/* Buttons */}
                  <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <button
                      className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                      onClick={() => navigate(`/${userId}/dashboard`)}
                    >
                      <FiX className="mr-2" /> İPTAL
                    </button>

                    <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={prevStep}
                        className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center hover:bg-green-50"
                      >
                        <FiChevronLeft className="mr-2" /> GERİ
                      </button>
                      <button
                        onClick={() => {
                          handleSubmit("DRAFT");
                        }}
                        disabled={isSubmitting}
                        className={`w-full sm:w-auto px-6 py-2 ${
                          isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                        } text-white rounded-full flex items-center justify-center min-w-24`}
                      >
                        {isSubmitting ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        ) : null}
                        {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                      </button>
                      <button
                        onClick={nextStep}
                        disabled={!isCurrentDayValid()}
                        className={`w-full sm:w-auto px-6 py-2 ${
                          !isCurrentDayValid() 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'green-button'
                        } text-white rounded-full flex items-center justify-center`}
                      >
                        İLERİ <FiChevronRight className="ml-2" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {currentStep === 3 && (
          <div className="form-step">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">
                  YAYINLAMA SEÇENEKLERİ
                </h3>
                <p className="text-gray-600 text-sm">
                  Kursunuzun ilk dersini vermek için en uygun zamanı seçin
                  (diğer tüm dersler her gün aynı saatte verilecektir).
                </p>
              </div>

              <div className="flex justify-start mt-2 mb-2">
                <select
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white text-gray-700 w-full max-w-md"
                  value={selectedGroupId}
                  onChange={(e) => setSelectedGroupId(Number(e.target.value))}
                >
                  <option value="">KATILIMCI LİSTESİ</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  YAYINLAMA TARİHİ:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryDate
                      ? new Date(formData.deliveryDate)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryDate: date
                        ? date.toLocaleDateString("en-CA") // YYYY-MM-DD formatı, yerel saatle
                        : "",
                      deliveryTime:
                        date &&
                        date.toDateString() === new Date().toDateString() &&
                        prev.deliveryTime <
                          new Date().toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                          ? new Date().toLocaleTimeString("tr-TR", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : prev.deliveryTime,
                    }))
                  }
                  dateFormat="dd-MM-yyyy"
                  locale={tr}
                  placeholderText="01-01-2000"
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  minDate={new Date()}
                  isClearable
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA SIKLIĞI:
                </label>
                <select
                  name="repostEvery"
                  value={formData.repostEvery}
                  onChange={handleInputChange}
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-32"
                >
                  <option value="daily">Gün</option>
                  <option value="weekly">Hafta</option>
                  <option value="monthly">Ay</option>
                </select>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <label className="text-gray-700 text-sm font-medium w-full sm:w-40">
                  TEKRARLAMA ZAMANI:
                </label>
                <DatePicker
                  selected={
                    formData.deliveryTime
                      ? new Date(`1970-01-01T${formData.deliveryTime}`)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      deliveryTime: date
                        ? date.toLocaleTimeString("tr-TR", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "",
                    }))
                  }
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={5}
                  timeCaption="Saat"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  placeholderText="00:00"
                  className="border border-gray-300 rounded-full px-4 py-2 bg-white w-full sm:w-auto"
                  isClearable
                  minTime={
                    formData.deliveryDate ===
                    new Date().toISOString().split("T")[0]
                      ? new Date()
                      : new Date(0, 0, 0, 0, 0)
                  }
                  maxTime={new Date(0, 0, 0, 23, 59)}
                />
              </div>
            </div>

            <div className="bg-white p-3 rounded-full inline-flex items-center mb-8 border border-gray-200">
              <input
                type="checkbox"
                id="whatsapp-checkbox"
                className="h-5 w-5 rounded border-gray-300 text-green-500 focus:ring-green-500"
                checked={formData.whatsappNotification}
                onChange={() =>
                  setFormData((prev) => ({
                    ...prev,
                    whatsappNotification: !prev.whatsappNotification,
                  }))
                }
              />
              <label
                htmlFor="whatsapp-checkbox"
                className="ml-2 flex items-center"
              >
                <FaWhatsapp className="text-green-500" size={24} />
              </label>
            </div>

            <div className="pt-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <button
                className="w-full md:w-auto px-6 py-2 red-button text-white rounded-full flex items-center justify-center"
                onClick={() => navigate(`/${userId}/dashboard`)}
              >
                <FiX className="mr-2" /> İPTAL
              </button>
              <div className="w-full md:w-auto flex flex-col sm:flex-row gap-3">
                <button
                  onClick={prevStep}
                  className="w-full sm:w-auto px-6 py-2 border border-green-500 text-green-500 bg-transparent rounded-full flex items-center justify-center"
                >
                  <FiChevronLeft className="mr-2" /> GERİ
                </button>
                <button
                  onClick={() => {
                    handleSubmit("DRAFT");
                  }}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "bg-[#07183c]"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isSubmitting ? "KAYDEDİLİYOR..." : "KAYDET"}
                </button>

                <button
                  onClick={() => handleSubmit("PUBLISHED")}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto px-6 py-2 ${
                    isSubmitting ? "bg-gray-400" : "green-button"
                  } text-white rounded-full flex items-center justify-center min-w-24`}
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isSubmitting ? (
                    "YAYINLANIYOR..."
                  ) : (
                    <>
                      YAYINLA <FiChevronRight className="ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <LinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        onSave={(url) => {
          setLessons((prev) =>
            prev.map((d, i) =>
              i === activeDay
                ? {
                    ...d,
                    media: {
                      ...d.media,
                      links: [...(d.media.links || []), url],
                    },
                  }
                : d
            )
          );
        }}
      />

      {/* INFO MODAL */}
      <InfoModal
        open={info.open}
        type={info.type}
        title={info.title}
        message={info.message}
        onClose={() => {
          closeInfo();
          if (info.type === "success") {
            navigate(`/${userId}/dashboard`);
          }
        }}
      />
    </SharedLayout>
  );
};

export default CreateCourse;
